import { Controller } from "@hotwired/stimulus";

/**
 * Beta Tester Signup Controller
 *
 * Handles client-side validation and form interactions for the beta tester signup form.
 * Works alongside the newsletter-form controller for Turbo form submission handling.
 *
 * Key Features:
 * - Validates at least one feature interest is selected before submission
 * - Shows/hides error messages dynamically
 * - Disables submit button during form submission
 * - Clears feature selection errors when user selects a checkbox
 *
 * Targets:
 * - form: The signup form element
 * - submitButton: The form submit button
 * - errorContainer: Container for displaying validation errors
 * - featureInterests: Container for feature interest checkboxes
 */
export default class extends Controller {
  static targets = ["form", "submitButton", "errorContainer", "featureInterests"];

  /**
   * Initialize controller
   */
  connect() {
    // Store original button text for restoration after submission
    if (this.hasSubmitButtonTarget) {
      this.originalButtonText = this.submitButtonTarget.textContent;
    }
  }

  /**
   * Validate form before submission
   * Called via data-action="submit->beta-tester-signup#validate"
   *
   * @param {Event} event - Form submit event
   * @returns {boolean} Whether validation passed
   */
  validate(event) {
    // Check if at least one feature interest is selected
    const checkboxes = this.element.querySelectorAll(
      'input[name="beta_tester_signup[feature_interests][]"]:checked'
    );

    if (checkboxes.length === 0) {
      event.preventDefault();
      this.showError("Please select at least one feature you're interested in.");

      // Scroll the feature interests section into view
      if (this.hasFeatureInterestsTarget) {
        this.featureInterestsTarget.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      return false;
    }

    // Clear any existing errors before submission
    this.hideError();

    // Disable submit button to prevent double submission
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
      this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
      this.submitButtonTarget.textContent = "Submitting...";
    }

    return true;
  }

  /**
   * Show error message in the error container
   *
   * @param {string} message - Error message to display
   */
  showError(message) {
    if (!this.hasErrorContainerTarget) return;

    this.errorContainerTarget.textContent = message;
    this.errorContainerTarget.classList.remove("hidden");

    // Apply error styling
    this.errorContainerTarget.classList.add(
      "p-3",
      "bg-red-900/20",
      "border",
      "border-red-700/30",
      "rounded"
    );

    // Re-enable submit button when showing validation errors
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false;
      this.submitButtonTarget.classList.remove("opacity-50", "cursor-not-allowed");
      this.submitButtonTarget.textContent = this.originalButtonText || "Join Beta Program";
    }

    // Ensure error is visible
    this.errorContainerTarget.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }

  /**
   * Hide the error container
   */
  hideError() {
    if (!this.hasErrorContainerTarget) return;

    this.errorContainerTarget.textContent = "";
    this.errorContainerTarget.classList.add("hidden");
    this.errorContainerTarget.classList.remove(
      "p-3",
      "bg-red-900/20",
      "border",
      "border-red-700/30",
      "rounded"
    );
  }

  /**
   * Clear feature interest validation error when user selects a checkbox
   * Called via data-action="change->beta-tester-signup#clearFeatureError"
   */
  clearFeatureError() {
    // Only clear if the error was about feature interests
    if (
      this.hasErrorContainerTarget &&
      this.errorContainerTarget.textContent.includes("feature")
    ) {
      this.hideError();
    }
  }

  /**
   * Reset form to initial state
   * Can be called programmatically if needed
   */
  reset() {
    // Re-enable submit button
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false;
      this.submitButtonTarget.classList.remove("opacity-50", "cursor-not-allowed");
      this.submitButtonTarget.textContent = this.originalButtonText || "Join Beta Program";
    }

    // Hide errors
    this.hideError();
  }
}
